import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatCurrency } from '../data';

const {
  FiShoppingBag,
  FiDollarSign,
  FiFileText,
  FiBox,
  FiArrowUpRight,
  FiEye,
  FiPlus,
  FiTrendingUp
} = FiIcons;

function AdminOverview({ metrics, loading, onNavigate }) {
  if (loading) {
    return <div className="admin-loading-state">Loading store statistics…</div>;
  }

  const awaitingPayment = metrics.awaitingPayment || 0;
  const awaitingConfirmation = metrics.awaitingConfirmation || 0;
  const processing = metrics.processing || 0;
  const delivered = metrics.delivered || 0;
  const readyToShip = metrics.readyToShip || 0;
  const totalOrdersCount = metrics.totalOrders || 0;

  return (
    <div className="admin-overview-container">
      {/* Top Welcome Title */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Good day, ZEVI Admin</h1>
          <p className="admin-page-subtitle">Here is what is happening with your store today.</p>
        </div>
        <div className="admin-header-actions">
          <button className="admin-btn-primary" onClick={() => onNavigate('products')}>
            <SafeIcon icon={FiPlus} />
            <span>Add Product</span>
          </button>
          <button className="admin-btn-secondary" onClick={() => onNavigate('orders')}>
            <SafeIcon icon={FiShoppingBag} />
            <span>View Orders</span>
          </button>
        </div>
      </div>

      {/* Top 4 KPI Metric Cards */}
      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">TOTAL ORDERS</span>
            <div className="kpi-icon-wrap orders">
              <SafeIcon icon={FiShoppingBag} />
            </div>
          </div>
          <strong className="kpi-value">{totalOrdersCount || 0}</strong>
          <div className="kpi-trend positive">
            <SafeIcon icon={FiArrowUpRight} />
            <span>Active store total</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-header">
            <span className="kpi-title">VERIFIED REVENUE</span>
            <div className="kpi-icon-wrap revenue">
              <SafeIcon icon={FiDollarSign} />
            </div>
          </div>
          <strong className="kpi-value">{formatCurrency(metrics.revenue || 0)}</strong>
          <div className="kpi-trend positive">
            <SafeIcon icon={FiTrendingUp} />
            <span>Confirmed and paid</span>
          </div>
        </div>

        <div
          className="admin-kpi-card clickable"
          onClick={() => onNavigate('payments')}
        >
          <div className="kpi-header">
            <span className="kpi-title">PAYMENT REVIEW</span>
            <div className="kpi-icon-wrap review">
              <SafeIcon icon={FiFileText} />
            </div>
          </div>
          <strong className="kpi-value">{awaitingPayment}</strong>
          <span className="kpi-note">Slips requiring verification</span>
        </div>

        <div
          className="admin-kpi-card clickable"
          onClick={() => onNavigate('inventory')}
        >
          <div className="kpi-header">
            <span className="kpi-title">PENDING CONFIRMATION</span>
            <div className="kpi-icon-wrap stock">
              <SafeIcon icon={FiBox} />
            </div>
          </div>
          <strong className="kpi-value">{awaitingConfirmation}</strong>
          <span className="kpi-note">New customer orders</span>
        </div>
      </div>

      {/* 2-Column Middle Grid: Orders Status + Quick Task Boxes */}
      <div className="admin-dashboard-two-col">
        {/* Order Status Breakdown */}
        <div className="admin-card-container">
          <div className="admin-card-header">
            <h3>Order Status Summary</h3>
            <span className="admin-tag-pill">{totalOrdersCount} Total</span>
          </div>

          <div className="order-status-list">
            <div className="order-status-row" onClick={() => onNavigate('orders')}>
              <div className="status-label-group">
                <span className="dot dot-blue" />
                <span>New Orders Received</span>
              </div>
              <span className="status-count">{awaitingConfirmation}</span>
            </div>

            <div className="order-status-row" onClick={() => onNavigate('payments')}>
              <div className="status-label-group">
                <span className="dot dot-orange" />
                <span>Payment Review (Slips)</span>
              </div>
              <span className="status-count badge-warn">{awaitingPayment}</span>
            </div>

            <div className="order-status-row" onClick={() => onNavigate('orders')}>
              <div className="status-label-group">
                <span className="dot dot-yellow" />
                <span>Processing / Packing</span>
              </div>
              <span className="status-count">{processing}</span>
            </div>

            <div className="order-status-row" onClick={() => onNavigate('orders')}>
              <div className="status-label-group">
                <span className="dot dot-teal" />
                <span>Ready to Ship</span>
              </div>
              <span className="status-count">{readyToShip}</span>
            </div>

            <div className="order-status-row" onClick={() => onNavigate('orders')}>
              <div className="status-label-group">
                <span className="dot dot-green" />
                <span>Delivered Successfully</span>
              </div>
              <span className="status-count">{delivered}</span>
            </div>
          </div>
        </div>

        {/* Cash On Delivery & Quick Tools */}
        <div className="admin-card-container">
          <div className="admin-card-header">
            <h3>Payment Summary</h3>
            <span className="admin-link" onClick={() => onNavigate('settings')}>Settings</span>
          </div>

          <div className="admin-summary-box">
            <div className="summary-item">
              <span className="summary-label">Cash on Delivery (Pending Collection)</span>
              <strong className="summary-amount">{formatCurrency(metrics.codUnpaid || 0)}</strong>
              <small>Cash to receive upon courier delivery</small>
            </div>

            <div className="admin-quick-links-group">
              <button className="admin-action-tile" onClick={() => onNavigate('payments')}>
                <div className="tile-text">
                  <strong>Review Bank & QR Slips</strong>
                  <span>Approve uploaded payment receipts</span>
                </div>
                <SafeIcon icon={FiEye} />
              </button>

              <button className="admin-action-tile" onClick={() => onNavigate('inventory')}>
                <div className="tile-text">
                  <strong>Check Stock Levels</strong>
                  <span>Monitor low and out-of-stock sarees</span>
                </div>
                <SafeIcon icon={FiEye} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminOverview;